/*
 *  ZAITEC CONFIDENTIAL
 *  __________________
 *
 * [2019] - [2020] zaiTEC Logixs Ltd
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of  zaiTEC Logixs Ltd and its suppliers,if any.
 *  The intellectual and technical concepts contained herein are proprietary to zaiTEC Logixs Ltd, and are protected by
 *  trade secret or copyright law.Dissemination of this information or reproduction of this material is strictly forbidden
 *  unless prior written permission is obtained from  zaiTEC Logixs Ltd.
 */

import { AuthService } from '@/app/shared';
import { AuthGuardService } from '@/app/shared/guard/authGuard.service';
import { AuthProvider } from '@/app/shared/model/AuthProvider';
import { LoginRequest } from '@/app/shared/model/LoginRequest';
import { Component, HostListener, OnInit, ViewEncapsulation } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { slideInAnimation } from './router-animations';
import { environment } from '@/environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OfflineNotificationComponent } from './shared/modules/offline-notification/offline-notification.component';
import { UtmTrackerService } from './shared/services/utm-tracker.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [slideInAnimation],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
  isOnline = navigator.onLine;

  token!: string;
  error!: string;
  authProvider: AuthProvider = AuthProvider.provider;
  isLoggedIn: boolean = false;
  constructor(
    private sanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry,
    private authGuardService: AuthGuardService,
    private authSnv: AuthService,
    private snackBar: MatSnackBar,
    private untTrackerService: UtmTrackerService,
    public router: Router,
  ) {}

  ngOnInit() {
    //this.authGuardService.removeTokensAndResetFlags();
    // Subscribe to get the latest value
    this.authGuardService.isLoggedIn$.subscribe((status) => {
      this.isLoggedIn = status;
    });

    this.initSvgIcons();
    this.getToken();

    // get the utm parameters from the URL
    this.untTrackerService.captureUtmParams();

    // Check if the user was forcefully logged out
    const forceLogout = sessionStorage.getItem('forceLogout');
    if (forceLogout === 'true') {
      sessionStorage.removeItem('forceLogout');
      this.authSnv.createSnakbarMessage();
      this.router.navigate(['/login'], { replaceUrl: true }); // Angular router navigation
    }
  }

  private initSvgIcons() {
    this.matIconRegistry.addSvgIcon(
      'google_logo',
      this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/google_logo.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'github_logo',
      this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/github_logo.svg'),
    );
  }

  getToken() {
    const loginRequest = new LoginRequest();
    loginRequest.email = environment.MASTER_EMAIL;
    loginRequest.password = environment.MASTER_PASSWORD;
    //console.log(loginRequest);

    const token = this.authGuardService.getToken();
    //console.log(token);
    if (token === undefined || token == null) {
      this.authSnv.login(loginRequest).subscribe({
        next: (data) => {
          const token = JSON.parse(JSON.stringify(data)).accessToken;
          if (token) {
            this.authProvider = AuthProvider.local;
            this.authGuardService.setAuthentication(token);
          }
        },
        error: (error) => (this.error = error.message),
      });
    }
  }

  // offline snack
  @HostListener('window:online')
  onOnline() {
    this.isOnline = true;
    this.snackBar.dismiss();
    this.snackBar.open('Back online 🎉', 'Close', {
      duration: 3000,
      panelClass: ['online-snackbar'],
    });
  }

  @HostListener('window:offline')
  onOffline() {
    this.isOnline = false;
    this.snackBar.openFromComponent(OfflineNotificationComponent, {
      duration: 0,
      panelClass: ['offline-snackbar'],
    });
  }
  /* Doc Applicaiton install prompt END */
  // Triggered before the page is unloaded (refresh, close, reload)
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    //check if the user is logged in
    if (this.isLoggedIn) {
      sessionStorage.setItem('forceLogout', 'true');
      this.authSnv.disconnectWSAndLogout(); // clear session and disconnect WebSocket
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleReloadShortcut(event: KeyboardEvent) {
    // Detect Ctrl+R or F5
    if ((event.ctrlKey && event.key === 'r') || event.key === 'F5') {
      //check if the user is logged in
      if (this.isLoggedIn) {
        event.preventDefault();
        alert('Refreshing will log you out. Please use Logout button or navigate safely.');
      }
    }
  }
}
